export type UploadDiagnosticsContext = {
    answerId?: string | null
    uploadKind?: 'file' | 'recording' | 'image'
    source?: string
    fileIndex?: number
    totalFiles?: number
}

export type UploadDiagnosticDetails = Record<string, unknown>

export type UploadErrorWithDiagnostics = Error & {
    uploadDiagnostics?: UploadDiagnosticDetails
    isUploadCancelled?: boolean
    // Shown to the user instead of the generic failure notice.
    userMessage?: string
}

export const UPLOAD_FAILED_MESSAGE = 'Upload failed. Please try again.'

export function uploadFailureMessage(error: unknown): string {
    return (error as UploadErrorWithDiagnostics | null)?.userMessage || UPLOAD_FAILED_MESSAGE
}

function formatBytes(bytes: number): string {
    if (bytes >= 1024 ** 3) return `${Math.round((bytes / 1024 ** 3) * 10) / 10} GB`
    return `${Math.max(1, Math.round(bytes / 1024 ** 2))} MB`
}

// Android cloud apps (Drive, OneDrive) can hand the browser a 0-byte File for a
// file that is not downloaded to the phone yet. Reading it tells that apart from
// a file that really is empty.
export type FileReadProbe = { bytes: number; error?: string }

export async function probeFileRead(file: Blob, timeoutMs = 3000): Promise<FileReadProbe> {
    let timer: ReturnType<typeof setTimeout> | undefined
    try {
        const reader = file.stream().getReader()
        const timeout = new Promise<never>((_, reject) => {
            timer = setTimeout(
                () => reject(Object.assign(new Error('timeout'), { name: 'Timeout' })),
                timeoutMs,
            )
        })
        const chunk = await Promise.race([reader.read(), timeout])
        reader.cancel().catch(() => {})
        return { bytes: chunk.value?.byteLength ?? 0 }
    } catch (error) {
        return { bytes: 0, error: error instanceof Error ? error.name : 'Error' }
    } finally {
        if (timer) clearTimeout(timer)
    }
}

// Rejects files the server would refuse at upload start, with a message that says why.
export function precheckUploadFile(
    file: { name?: string; size: number; lastModified?: number },
    maxBytes?: number | null,
    probe?: FileReadProbe,
): UploadErrorWithDiagnostics | null {
    const name = file.name ? `"${file.name}"` : 'This file'
    const lastModifiedAge =
        typeof file.lastModified === 'number' && file.lastModified > 0
            ? Math.round((Date.now() - file.lastModified) / 1000)
            : undefined
    if (file.size === 0) {
        const error = annotateUploadError(new Error('empty file'), {
            stage: 'precheck',
            reason: 'empty_file',
            file_last_modified_age_s: lastModifiedAge,
            probe_bytes: probe?.bytes,
            probe_error: probe?.error,
        })
        error.userMessage = `${name} came through as 0 bytes. If it's in Google Drive or another cloud app, open it once or save it to your device, then attach it again.`
        return error
    }
    if (maxBytes && file.size > maxBytes) {
        const error = annotateUploadError(new Error('file too large'), {
            stage: 'precheck',
            reason: 'too_large',
            upload_max_bytes: maxBytes,
        })
        error.userMessage = `${name} is ${formatBytes(
            file.size,
        )}. The upload limit is ${formatBytes(maxBytes)}.`
        return error
    }
    return null
}

// The server's own reason for a refused upload start (size limit, daily quota).
export function serverUploadRejectionMessage(
    status: number,
    responseText: string,
): string | undefined {
    if (status !== 413 && status !== 429) return undefined
    try {
        const error = JSON.parse(responseText)?.error
        if (typeof error === 'string' && error)
            return error.charAt(0).toUpperCase() + error.slice(1)
    } catch {}
    return undefined
}

export function getFileExtension(name?: string): string | undefined {
    if (!name) return undefined
    const ext = name.split('.').pop()?.trim().toLowerCase()
    if (!ext || ext === name.toLowerCase()) return undefined
    return ext
}

export function buildSafeUploadFileMetadata(file: {
    name?: string
    size?: number
    type?: string
}): UploadDiagnosticDetails {
    return {
        file_size: file.size,
        file_name_length: file.name?.length,
        file_extension: getFileExtension(file.name),
        content_type: file.type || 'application/octet-stream',
    }
}

export function annotateUploadError(
    error: unknown,
    details: UploadDiagnosticDetails = {},
): UploadErrorWithDiagnostics {
    const err = error instanceof Error ? error : new Error(String(error))
    const withDiagnostics = err as UploadErrorWithDiagnostics
    withDiagnostics.uploadDiagnostics = {
        ...(withDiagnostics.uploadDiagnostics || {}),
        ...details,
    }
    return withDiagnostics
}

export function createUploadCancelledError(
    details: UploadDiagnosticDetails = {},
): UploadErrorWithDiagnostics {
    const err = annotateUploadError(new Error('Upload cancelled'), {
        ...details,
        cancelled: true,
    })
    err.name = 'UploadCancelledError'
    err.isUploadCancelled = true
    return err
}

export function isUploadCancellationError(error: unknown): boolean {
    if (!(error instanceof Error)) return false
    const uploadError = error as UploadErrorWithDiagnostics
    return (
        uploadError.isUploadCancelled === true ||
        uploadError.name === 'UploadCancelledError' ||
        uploadError.uploadDiagnostics?.cancelled === true
    )
}

export function captureUploadFailureWithReporter(
    reporter: {
        captureException: (error: Error) => void
        addBreadcrumb: (message: string, details: UploadDiagnosticDetails) => void
    },
    error: unknown,
    details: UploadDiagnosticDetails = {},
): UploadErrorWithDiagnostics {
    const err = annotateUploadError(error, details)
    if (isUploadCancellationError(err)) {
        reporter.addBreadcrumb('upload_cancelled', err.uploadDiagnostics || details)
        return err
    }

    reporter.captureException(err)
    return err
}
